import type { EntityDto } from "api/types";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import LineIcon from "./LineIcon";
import SidebarEntityItem from "./SidebarEntityItem";
import type { SidebarEntityGroupProps } from "./treeEditor.types";

import styles from "./SidebarEntityGroup.module.css";

/** How many entities a group lists before collapsing the rest behind "Show more". */
const MAX_VISIBLE_ITEMS = 5;

const SidebarEntityGroup = ({
	group,
	isCompact,
	isExpanded,
	items,
	onCompactSelect,
	onSelect,
	onToggle,
	selectedEntityId
}: SidebarEntityGroupProps) => {
	const { t } = useTranslation("homePage");
	const onToggleClick = useCallback((): void => onToggle(group.type), [group.type, onToggle]);
	const onCompactClick = useCallback((): void => onCompactSelect(group.type), [group.type, onCompactSelect]);
	const [showAll, setShowAll] = useState(false);
	const toggleShowAll = useCallback((): void => setShowAll((current: boolean) => !current), []);
	const visibleItems = showAll ? items : items.slice(0, MAX_VISIBLE_ITEMS);
	const chevronClassName = isExpanded
		? `${styles.entityGroupChevron} ${styles.entityGroupChevronOpen}`
		: styles.entityGroupChevron;
	const groupLabel = t(`editor.entityGroups.${group.type}`);

	if (isCompact) {
		return (
			<button
				aria-label={`${groupLabel} (${items.length})`}
				className={styles.compactGroupButton}
				onClick={onCompactClick}
				title={`${groupLabel} (${items.length})`}
				type="button">
				<LineIcon name={group.icon} />
				<span className={styles.compactGroupCount}>{items.length}</span>
			</button>
		);
	}

	return (
		<div>
			<button
				aria-expanded={isExpanded}
				className={styles.entityGroupButton}
				onClick={onToggleClick}
				type="button">
				<span className={styles.entityGroupLabel}>
					<LineIcon name={group.icon} />
					<span>
						{groupLabel} ({items.length})
					</span>
				</span>
				<span className={chevronClassName}>
					<LineIcon name="chevronDown" />
				</span>
			</button>

			{isExpanded && items.length > 0 && (
				<ul className={styles.entityItemList}>
					{visibleItems.map((entity: EntityDto) => (
						<SidebarEntityItem
							key={entity.id}
							entity={entity}
							isActive={entity.id === selectedEntityId}
							onSelect={onSelect}
						/>
					))}

					{items.length > MAX_VISIBLE_ITEMS && (
						<li>
							<button className={styles.showMoreButton} onClick={toggleShowAll} type="button">
								{showAll
									? t("editor.showLessEntities")
									: t("editor.showMoreEntities", { count: items.length - MAX_VISIBLE_ITEMS })}
							</button>
						</li>
					)}
				</ul>
			)}
		</div>
	);
};

export default SidebarEntityGroup;
